import { Button, Menu } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { IconCamera, IconChevronDown, IconPhoto } from '@tabler/icons-react'
import { useRef } from 'react'

interface AddPhotoButtonProps {
  onFileSelect: (file: File) => void
  loading?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'compact-md'
  variant?: string
}

export function AddPhotoButton({
  onFileSelect,
  loading = false,
  size = 'xs',
  variant = 'outline',
}: AddPhotoButtonProps) {
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)

  // Global localStorage preference - shared across all instances
  const [lastAction, setLastAction] = useLocalStorage<'camera' | 'gallery'>({
    key: 'photo-upload-method',
    defaultValue: 'camera',
  })

  const handleAction = (action: 'camera' | 'gallery') => {
    setLastAction(action)
    if (action === 'camera') {
      cameraInputRef.current?.click()
    } else {
      galleryInputRef.current?.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onFileSelect(file)
      e.target.value = '' // Reset to allow selecting the same file again
    }
  }

  const Icon = lastAction === 'camera' ? IconCamera : IconPhoto

  return (
    <>
      {/* Hidden camera input */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      {/* Hidden gallery input */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Split-button UI */}
      <Button.Group>
        <Button
          variant={variant}
          size={size}
          leftSection={variant === 'subtle' ? null : <Icon size={14} />}
          onClick={() => handleAction(lastAction)}
          loading={loading}
          style={{ minHeight: size === 'compact-md' ? 40 : undefined }}
        >
          Lisää kuva
        </Button>
        <Menu position="bottom-end">
          <Menu.Target>
            <Button
              variant={variant}
              size={size}
              style={{
                borderLeft: 'none',
                minHeight: size === 'compact-md' ? 40 : undefined,
              }}
            >
              <IconChevronDown size={12} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconCamera size={16} />}
              onClick={() => handleAction('camera')}
            >
              Ota kuva
            </Menu.Item>
            <Menu.Item
              leftSection={<IconPhoto size={16} />}
              onClick={() => handleAction('gallery')}
            >
              Valitse galleriasta
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    </>
  )
}
